import type { StoryElement } from '../types';
import {
  preDecaySubtotal as computePreDecaySubtotal,
  readSocialComponent,
  sourceBoost as computeSourceBoost,
} from './baseScoreDetails';
import { formatAge, formatDate } from './formatTime';

export function fmt(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return '-';
  return value.toFixed(digits).replace(/\.?0+$/, '');
}

export function fmtSigned(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return '+0';
  const rounded = fmt(Math.abs(value), digits);
  return `${value < 0 ? '-' : '+'}${rounded}`;
}

// Format a timestamp for display, returning '-' rather than the string
// "Invalid Date" when the value is missing or unparseable (e.g. an expired
// score's reset blob carries no when_computed).
// The relative age in parentheses ("(3h ago)", "(in 2d)") saves reading the
// full timestamp against the clock.
export function fmtDateTime(value: string | number | null | undefined, now = Date.now()): string {
  const absolute = formatDate(value, { fallback: '-' });
  if (absolute === '-') return absolute;
  const at = new Date(value!).getTime();
  // formatAge measures a past instant against `now`, so a future one swaps the two.
  const relative = at <= now
    ? formatAge(new Date(at).toISOString(), { now })
    : `in ${formatAge(new Date(now).toISOString(), { compact: true, now: at })}`;
  return `${absolute} (${relative})`;
}

// The score chain below is the TS mirror of the backend owner,
// backend/app/services/feed_score_helpers.py (OWN_SCORE_CHAIN /
// DISPLAY_SCORE_CHAIN, implemented by positional_score / get_feed_score_key);
// iOS and Android carry their own mirrors in each platform's
// ScoreBreakdownMath file. The spelling here is parity-pinned by
// backend/tests/test_ranking_chain_mirror_parity.py. A zero score carries no
// ranking signal on the backend (falsy `or` chain), so it falls through here
// too rather than short-circuiting the chain.

/** A story's own ranking key: feed_score, else the curator-adjusted
 * composite_score, else the raw base_score. */
export function ownScore(story: StoryElement): number | null {
  return story.feed_score || story.composite_score || story.base_score || null;
}

/** The score that determined the story's position in a legacy-ranked list
 * (/fulltextsearch): the score its slot was claimed with (cluster_rank_score,
 * for a clustered winner) over the story's own ranking key. Feeds v2 lists are
 * ordered by rank_score instead (utils/rankAudit). */
export function displayScore(story: StoryElement): number | null {
  return story.cluster_rank_score || ownScore(story);
}

export interface ScoreBreakdown {
  displayScore: number | null | undefined;
  clusterRankScore: number | null | undefined;
  ownScore: number | null | undefined;
  baseScoreWithoutRelevance: number | null | undefined;
  relevanceFactor: number | undefined;
  feedScore: number | null | undefined;
  relevanceScore: number | null | undefined;
  sourceAdjustment: number;
  sourceBoost: number;
  socialAdjustment: number;
  curatorAdjustment: number;
  paperSignificanceAdjustment: number;
  sentenceQualityAdjustment: number;
  preDecaySubtotal: number | null;
  stalePenalty: number;
  redundantPenalty: number;
  sourcePenalty: number;
  knownAdjustmentPenalty: number;
  otherAdjustmentPenalty: number;
  sourcePenaltyLabel: string;
}

export function computeScoreBreakdown(story: StoryElement): ScoreBreakdown {
  const d = story.base_score_details;
  const own = ownScore(story);
  const clusterRankScore = story.cluster_rank_score;
  const display = displayScore(story);
  const baseScoreWithoutRelevance = d?.base_score_without_relevance ?? story.base_score;
  const relevanceFactor = d?.relevance_factor;
  const feedScore = d?.feed_score ?? story.feed_score;
  const relevanceScore = d?.relevance_score ?? story.relevance_score;
  const sourceAdjustment = d?.source_adjustment ?? 0;
  const sourceBoost = d ? computeSourceBoost(d) : Math.max(sourceAdjustment, 0);
  const socialAdjustment = d ? readSocialComponent(d) : 0;
  const curatorAdjustment = d?.curator_adjustment ?? 0;
  const paperSignificanceAdjustment = d?.paper_significance_adjustment ?? 0;
  const sentenceQualityAdjustment = d?.sentence_quality_adjustment ?? 0;
  const preDecaySubtotal = d
    ? computePreDecaySubtotal({
        tier: d.tier,
        effectiveBoost: d.effective_boost,
        sourceBoost,
        socialBoost: socialAdjustment,
        curatorAdjustment,
        paperSignificanceAdjustment,
        sentenceQualityAdjustment,
        gatedPenalty: d.gated_penalty,
        adjPenalty: d.adj_penalty,
      })
    : null;
  // Penalty split: named rows for the entries the backend owner
  // BREAKDOWN_NAMED_PENALTY_KEYS (backend/app/services/score_adjustments.py)
  // designates, plus a clamped residual "other" row for everything else in
  // adj_penalty (today: the social floor). This spelling is parity-pinned by
  // backend/tests/test_penalty_split_mirror_parity.py; iOS and Android carry
  // the same rule in their ScoreBreakdownMath files.
  const adjustmentPenalties = d?.adjustment_penalties;
  const stalePenalty = adjustmentPenalties?.stale ?? 0;
  const redundantPenalty = adjustmentPenalties?.redundant ?? 0;
  const sourcePenalty = adjustmentPenalties?.source ?? Math.max(-sourceAdjustment, 0);
  const knownAdjustmentPenalty = stalePenalty + redundantPenalty + sourcePenalty;
  const otherAdjustmentPenalty = d ? Math.max(d.adj_penalty - knownAdjustmentPenalty, 0) : 0;
  const sourcePenaltyLabel = d?.source_score != null
    ? `Source quality penalty (score ${d.source_score})`
    : 'Source quality penalty';

  return {
    displayScore: display,
    clusterRankScore,
    ownScore: own,
    baseScoreWithoutRelevance,
    relevanceFactor,
    feedScore,
    relevanceScore,
    sourceAdjustment,
    sourceBoost,
    socialAdjustment,
    curatorAdjustment,
    paperSignificanceAdjustment,
    sentenceQualityAdjustment,
    preDecaySubtotal,
    stalePenalty,
    redundantPenalty,
    sourcePenalty,
    knownAdjustmentPenalty,
    otherAdjustmentPenalty,
    sourcePenaltyLabel,
  };
}
